import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronRight, Clock, EyeOff, Lock, SquareKanban } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { useCurrentUser } from '../lib/auth.js';
import { FullPageState, Spinner } from '../components/ui.js';

export function JoinPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading: loadingUser } = useCurrentUser();
  const [name, setName] = useState('');

  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.invites.preview(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const redeem = useMutation({
    mutationFn: (displayName?: string) => api.invites.redeem(token!, displayName),
    onSuccess: (result) => {
      if (result.kind === 'RETRO') navigate(`/retro/${result.retroId}`, { replace: true });
      else navigate(`/teams/${result.teamId}`, { replace: true });
    },
  });

  if (loadingUser || preview.isLoading) {
    return (
      <div className="signin">
        <Spinner label="Checking your invite…" />
      </div>
    );
  }

  if (preview.error) {
    return (
      <FullPageState title="This invite link isn’t valid">
        <p>It may have been replaced, or the retrospective may have been deleted.</p>
        <Link to="/" className="btn btn-ghost mt-4 inline-flex">
          Go to the app
        </Link>
      </FullPageState>
    );
  }

  const data = preview.data!;
  const error = redeem.error instanceof ApiError ? redeem.error.message : null;

  if (data.kind === 'TEAM') {
    return (
      <Shell eyebrow="You’re invited to" title={data.team.name} subtitle="a team on retro.blue">
        {user ? (
          <button
            type="button"
            className="btn btn-primary btn-block btn-lg"
            onClick={() => redeem.mutate(undefined)}
            disabled={redeem.isPending}
          >
            {redeem.isPending ? 'Joining…' : `Join ${data.team.name}`}
          </button>
        ) : (
          <SignInPrompt token={token!} />
        )}
        {error ? <ErrorNote message={error} /> : null}
      </Shell>
    );
  }

  const { retro, mode } = data;

  // The server refuses every redemption for a closed retro; this is the
  // friendly face of that refusal rather than an error after clicking.
  if (retro.phase === 'CLOSED') {
    return (
      <Shell eyebrow="You’re invited to" title={retro.name} subtitle={`${retro.templateName} · ${retro.teamName}`}>
        <div className="notice-warn" role="status">
          <Clock size={15} className="i mt-0.5 flex-none" aria-hidden />
          <span>
            This retrospective has ended — it is no longer accepting new participants. People who took
            part can still open the board and see the report.
          </span>
        </div>
        <Link to="/" className="btn btn-ghost btn-block">
          Go to the app
        </Link>
      </Shell>
    );
  }

  return (
    <Shell
      eyebrow="You’re invited to"
      title={retro.name}
      subtitle={`${retro.templateName} · ${retro.teamName}`}
    >
      {mode === 'MEMBER' ? (
        user ? (
          <>
            <p className="text-[13px] text-muted">
              You’ll join the retrospective and the team, with access to its history.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-block btn-lg"
              onClick={() => redeem.mutate(undefined)}
              disabled={redeem.isPending}
            >
              {redeem.isPending ? 'Joining…' : 'Join the retro'}
            </button>
          </>
        ) : (
          <>
            <p className="text-[13px] text-muted">
              This link adds you to the team, so it needs a free account.
            </p>
            <SignInPrompt token={token!} />
          </>
        )
      ) : (
        <>
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              redeem.mutate(name.trim());
            }}
          >
            {user ? (
              <p className="text-[13px] text-muted">
                You’re signed in as {user.name}. You’ll join as a guest for this session — you won’t be
                added to the team.
              </p>
            ) : (
              <label className="field-l">
                Your name
                <input
                  className="input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Jamie Lee"
                  maxLength={60}
                  required
                  autoFocus
                  // Outside the label, so the field is announced as "Your name".
                  aria-describedby="guest-name-hint"
                />
                <span id="guest-name-hint" className="hint">
                  Shown next to your cards unless you post anonymously.
                </span>
              </label>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg"
              disabled={redeem.isPending || (!user && !name.trim())}
            >
              {redeem.isPending ? 'Joining…' : 'Join retrospective'}
              <ChevronRight size={15} aria-hidden />
            </button>
          </form>

          <ul className="info-list">
            <li>
              <SquareKanban size={14} className="i" aria-hidden />
              <span>
                You can add cards, react, vote and help agree actions in{' '}
                <strong>this session only</strong>.
              </span>
            </li>
            <li>
              <EyeOff size={14} className="i" aria-hidden />
              <span>You won’t see the team’s history, other sessions or the action board.</span>
            </li>
            <li>
              <Clock size={14} className="i" aria-hidden />
              <span>Guest access ends when the facilitator closes the session.</span>
            </li>
          </ul>
        </>
      )}

      {error ? <ErrorNote message={error} /> : null}

      {mode === 'GUEST' && !user ? (
        <p className="fine text-center">
          Have an account?{' '}
          <Link className="link" to={`/login?next=${encodeURIComponent(`/join/${token}`)}`}>
            Sign in instead
          </Link>
        </p>
      ) : null}
    </Shell>
  );
}

function Shell({
  eyebrow,
  title,
  subtitle,
  children,
}: React.PropsWithChildren<{ eyebrow: string; title: string; subtitle: string }>) {
  return (
    <div className="signin">
      <div className="signin-card">
        <div className="brand-lg">
          <span className="grid size-11 flex-none place-items-center rounded-xl bg-navy text-lg font-extrabold text-white">
            r.
          </span>
          <div>
            <strong>retro.blue</strong>
            <span>Guest access · no account needed</span>
          </div>
        </div>
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="mt-1 text-[22px] font-bold">{title}</h2>
          <p className="lede">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function SignInPrompt({ token }: { token: string }) {
  const next = encodeURIComponent(`/join/${token}`);
  // The invite travels with the link to the sign-up form, because holding one is
  // what permits creating an account on an instance with registration closed.
  const invite = encodeURIComponent(token);
  return (
    <div className="grid gap-2">
      <Link to={`/register?next=${next}&invite=${invite}`} className="btn btn-primary btn-block btn-lg">
        Create a free account
      </Link>
      <Link to={`/login?next=${next}`} className="btn btn-block">
        I already have an account
      </Link>
      <p className="fine flex items-center justify-center gap-1.5">
        <Lock size={11} aria-hidden /> retro.blue is internal — employees only.
      </p>
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <p role="alert" className="err-msg">
      {message}
    </p>
  );
}
